// BrainActivation.jsx
// Dummy "Brain Activation" page — layout reference: BrainDashboardDesign.pdf
// Panels: Activation, Improvements, Shift in Brain Region (heatmaps), GP Recommendation, Coherence Map.
// All data below is placeholder/dummy — wire to real EEG analytics API when available.

import React, { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import FormControl from "@mui/material/FormControl";
import MuiSelect from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

// Images
import humanBrainImage from "assets/images/illustrations/human-brain.jpg";

/* ---------- Dummy data ---------- */
const COHERENCE_CHART = {
  labels: ["1", "4", "8", "12", "16", "20", "24", "28", "32"],
  datasets: [
    {
      label: "Coherence",
      color: "info",
      data: [0.42, 0.58, 0.71, 0.65, 0.52, 0.48, 0.55, 0.6, 0.5],
    },
  ],
};

const WEEK_OPTIONS = ["Week 1", "Week 2", "Week 3", "Week 4"];

/* ---------- Reusable panel header (matches Card pattern used across the app) ---------- */
const PanelHeader = ({ icon, title, color = "dark" }) => (
  <MDBox display="flex">
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="4rem"
      height="4rem"
      variant="gradient"
      bgColor={color}
      color="white"
      shadow="md"
      borderRadius="xl"
      ml={3}
      mt={-2}
    >
      <Icon fontSize="medium" color="inherit">{icon}</Icon>
    </MDBox>
    <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
      {title}
    </MDTypography>
  </MDBox>
);

/* ---------- Topographic heatmap placeholder (dummy visual, not real EEG data) ---------- */
const HeatmapPlaceholder = ({ week, onWeekChange }) => (
  <MDBox display="flex" flexDirection="column" alignItems="center" sx={{ width: "100%" }}>
    <MDBox
      sx={{
        width: 140,
        height: 140,
        borderRadius: "50%",
        background:
          "radial-gradient(circle at 35% 35%, #ff4d4d 0%, #ffb84d 25%, #ffe34d 45%, #4dd2ff 70%, #2e6bff 100%)",
        boxShadow: "inset 0 0 20px rgba(0,0,0,0.25)",
        border: "3px solid #344767",
      }}
    />
    <MDBox mt={1.5} sx={{ minWidth: 130 }}>
      <FormControl size="small" fullWidth>
        <MuiSelect value={week} onChange={(e) => onWeekChange(e.target.value)} sx={{ fontSize: 13 }}>
          {WEEK_OPTIONS.map((w) => (
            <MenuItem key={w} value={w}>{w}</MenuItem>
          ))}
        </MuiSelect>
      </FormControl>
    </MDBox>
  </MDBox>
);

function BrainActivation() {
  const [topWeek, setTopWeek] = useState("Week 1");
  const [shiftWeekLeft, setShiftWeekLeft] = useState("Week 1");
  const [shiftWeekRight, setShiftWeekRight] = useState("Week 4");

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* page-level week selector */}
        <MDBox display="flex" justifyContent="flex-end" mb={2}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <MuiSelect value={topWeek} onChange={(e) => setTopWeek(e.target.value)} sx={{ fontSize: 14 }}>
              {WEEK_OPTIONS.map((w) => (
                <MenuItem key={w} value={w}>{w}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </MDBox>

        {/* Row 1: Brain visual + Activation */}
        <Grid container spacing={3} mb={1}>
          <Grid item xs={12} md={6}>
            <Card sx={{ width: "100%", height: "100%" }}>
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={4}
              >
                <MDBox
                  component="img"
                  src={humanBrainImage}
                  alt="Human brain — lobe regions"
                  sx={{ width: "85%", maxWidth: 320, borderRadius: "12px" }}
                />
                <MDTypography variant="button" color="text" mt={2}>
                  Brain Activation Map
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ width: "100%", height: "100%" }}>
              <PanelHeader icon="bolt" title="Activation" color="info" />
              <MDBox px={4} pt={1} pb={3}>
                <MDTypography variant="button" color="text" fontStyle="italic">
                  Dummy placeholder — activation summary text goes here once connected to the
                  EEG analytics API.
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Row 2: Improvements + Shift in Brain Region */}
        <Grid container spacing={3} mb={1}>
          <Grid item xs={12} md={6}>
            <Card sx={{ width: "100%", height: "100%" }}>
              <PanelHeader icon="trending_up" title="Improvements" color="success" />
              <MDBox px={4} pt={1} pb={3}>
                <MDTypography variant="button" color="text" fontStyle="italic">
                  Dummy placeholder — week-over-week improvement notes go here.
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ width: "100%", height: "100%" }}>
              <PanelHeader icon="compare" title="Shift in Brain Region Activation" color="dark" />
              <MDBox px={4} pt={1} pb={3}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <HeatmapPlaceholder week={shiftWeekLeft} onWeekChange={setShiftWeekLeft} />
                  </Grid>
                  <Grid item xs={6}>
                    <HeatmapPlaceholder week={shiftWeekRight} onWeekChange={setShiftWeekRight} />
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Row 3: GP Recommendation + Coherence Map */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ width: "100%", height: "100%" }}>
              <PanelHeader icon="medical_information" title="GP Recommendation" color="warning" />
              <MDBox px={4} pt={1} pb={3}>
                <MDTypography variant="button" color="text" fontStyle="italic">
                  Dummy placeholder — clinician recommendation text goes here.
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <DefaultLineChart
              icon={{ color: "dark", component: "show_chart" }}
              title="Coherence Map"
              description="EEG Coherence Data — frequency-wise coherence measurement from EEG signals (dummy data)"
              height="14rem"
              chart={COHERENCE_CHART}
            />
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default BrainActivation;
